/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {Component, Prop, Vue} from 'vue-property-decorator';

/**
 * Name of a custom CSS variable, that is set globally on the application level, where the value
 * of the `horizOffset` of the menu is written.
 */
export const horizOffsetCSSVariable = '--selection-menus--horiz-offset';

/**
 * A base class for single and multiple selection menus that contains the common aspects for
 * both of them.
 */
@Component
export class SelectionMenuBase<I> extends Vue {

  /**
   * A list of items for which the selection menu should be displayed.
   */
  @Prop()
  readonly items!: I[];

  /**
   * Each item must be associated with a unique key. A key is a string or a number that uniquely
   * identifies an item, which is required for correctly updating the rendered item list.
   *
   * This prop allows specifying a function that will be invoked by the component with an item as
   * its parameter, and should return a respective item key.
   *
   * This prop can be omitted if items are unique numbers or strings themselves, and can serve
   * as item keys.
   */
  @Prop()
  readonly itemKey?: (item: I) => string | number

  /**
   * A horizontal distance that a popup menu should be moved to the right relative to its default
   * position. This is useful for aligning the popup content with the invoker's content according
   * to application's design.
   */
  @Prop({default: '0px'})
  readonly horizOffset!: string;

  /**
   * Should be invoked by the subclass when menu's visibility changes.
   *
   * This method makes sure that the `--selection-menus--horiz-offset` custom CSS variable is
   * updated
   * @param visible
   * @protected
   */
  protected handleVisibleChanged(visible: boolean): void {
    if (visible) {
      this.changeHorizOffsetCssProperty();
    }
  }

  /**
   * Writes the value of the `horizOffset` prop to a global (application-level) custom CSS property
   * whose name is set in the `horizOffsetCSSVariable` variable.
   *
   * This variable needs to be set globally since menus are placed outside of component's child DOM
   * and hence not affected by the component's CSS declarations that normally go at the level of
   * usual component's place in the DOM tree.
   *
   * Hence, such a CSS declaration is "uplifted" in the DOM tree using
   * such a (store ==> LservApp) loop.
   */
  private changeHorizOffsetCssProperty() {
    this.$store.commit('setSelectionMenusHorizOffset', {
      horizOffset: this.horizOffset
    });
  }

  /**
   * @return name of the provided value's type
   */
  protected itemTypeStr(
      result: Record<string, unknown> | string | number | boolean | null | undefined
  ): string {
    return (typeof result) + ('constructor' in (result as Record<string, unknown>)
        ? (': ' + (result as Record<string, unknown>).constructor.name)
        : '');
  }

  /**
   * Obtains an item key, which can be used in `v-for` loops for a given item.
   */
  protected getItemKey(item: I): string | number {
    if (this.itemKey) {
      const result = this.itemKey(item);
      if (!this.isValidKeyType(result)) {
        throw (
            'The function specified with the `itemKey` prop must return either a string or ' +
            'a number, but it returned: ' + this.itemTypeStr(result)
        );
      }
      return result;
    }

    if (this.isValidKeyType(item)) {
      return item as unknown as (string | number);
    }

    throw (
        'The `itemKey` prop must be specified if items are not of string or number type: ' +
        typeof item
    );
  }

  /**
   * Checks whether the passed value can serve as an "item key" value, according to its type
   * (doesn't check whether it's unique or not though).
   */
  protected isValidKeyType<I>(item: I): boolean {
    return typeof item === 'number' || typeof item === 'string';
  }

}
